import { useCallback, useState } from "react";
import JSZip from "jszip";
import adminApiClient from "../../../api/admin";
import { Klydo } from "../../../api/klydo";
import {
  ArtistUser,
  buildArtistsXml,
  downloadXml,
  urlBaseName,
} from "../utils/artistsXml";

type Progress = { done: number; total: number };

// Bounded-concurrency fan-out; onDone ticks after each settle for progress.
async function forEachLimited<T>(
  items: T[],
  limit: number,
  worker: (item: T) => Promise<void>,
  onDone: () => void,
): Promise<void> {
  let cursor = 0;
  const runNext = async (): Promise<void> => {
    const index = cursor;
    cursor += 1;
    if (index >= items.length) return;
    try {
      await worker(items[index]);
    } finally {
      onDone();
    }
    await runNext();
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, () => runNext()),
  );
}

const uniqueCreators = (klydos: Klydo[]): string[] =>
  Array.from(new Set(klydos.map((k) => k.creator).filter(Boolean)));

const useFirmwareArtists = () => {
  const [isExportingXml, setIsExportingXml] = useState(false);
  const [isDownloadingImages, setIsDownloadingImages] = useState(false);
  const [progress, setProgress] = useState<Progress>({ done: 0, total: 0 });

  // Fetch each unique creator's artist profile (/admin/users/:id). Failures are
  // skipped so one missing/deleted artist doesn't abort the whole export.
  const fetchArtists = useCallback(
    async (creators: string[]): Promise<ArtistUser[]> => {
      const artists: ArtistUser[] = [];
      setProgress({ done: 0, total: creators.length });
      await forEachLimited(
        creators,
        6,
        async (creatorId) => {
          try {
            const user = (await adminApiClient.getUserById(
              creatorId,
            )) as unknown as ArtistUser;
            if (user?.id) artists.push(user);
          } catch (e) {
            console.warn(`Failed to fetch artist ${creatorId}`, e);
          }
        },
        () => setProgress((p) => ({ ...p, done: p.done + 1 })),
      );
      return artists;
    },
    [],
  );

  const exportArtistsXml = useCallback(
    async (klydos: Klydo[], firmwareName: string) => {
      const creators = uniqueCreators(klydos);
      if (!creators.length) return;
      setIsExportingXml(true);
      try {
        const artists = await fetchArtists(creators);
        if (!artists.length) throw new Error("No artists could be fetched");
        const safeName = (firmwareName || "firmware").replace(
          /[^a-zA-Z0-9_-]/g,
          "_",
        );
        downloadXml(`${safeName}-artists.xml`, buildArtistsXml(artists));
      } catch (error) {
        console.error("Failed to export artists xml:", error);
      } finally {
        setIsExportingXml(false);
      }
    },
    [fetchArtists],
  );

  const downloadArtistImages = useCallback(
    async (klydos: Klydo[], firmwareName: string) => {
      const creators = uniqueCreators(klydos);
      if (!creators.length) return;
      setIsDownloadingImages(true);
      try {
        const artists = await fetchArtists(creators);
        const withPhoto = artists.filter((a) => !!a.photoUrl);
        if (!withPhoto.length) throw new Error("No artist photos to download");

        const zip = new JSZip();
        const usedNames = new Set<string>();
        let added = 0;
        setProgress({ done: 0, total: withPhoto.length });

        await forEachLimited(
          withPhoto,
          6,
          async (artist) => {
            const response = await fetch(artist.photoUrl as string);
            if (!response.ok) {
              throw new Error(`Failed to fetch photo for ${artist.id}`);
            }
            const blob = await response.blob();
            let name =
              urlBaseName(artist.photoUrl as string) || `${artist.id}.jpg`;
            if (usedNames.has(name)) name = `${artist.id}-${name}`;
            usedNames.add(name);
            zip.file(name, blob);
            added += 1;
          },
          () => setProgress((p) => ({ ...p, done: p.done + 1 })),
        );

        if (added === 0) throw new Error("No artist photos could be fetched");

        const safeName = (firmwareName || "firmware").replace(
          /[^a-zA-Z0-9_-]/g,
          "_",
        );
        const zipBlob = await zip.generateAsync({ type: "blob" });
        const objectUrl = URL.createObjectURL(zipBlob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = `${safeName}-artist-images.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
      } catch (error) {
        console.error("Failed to download artist images:", error);
      } finally {
        setIsDownloadingImages(false);
      }
    },
    [fetchArtists],
  );

  return {
    exportArtistsXml,
    downloadArtistImages,
    isExportingXml,
    isDownloadingImages,
    progress,
  };
};

export default useFirmwareArtists;
